/**
 * Справочники по играм: жанры, возраст, форматирование.
 *
 * API отдаёт жанр строкой на английском (`platformer`, `shooter`…),
 * а показывать надо по-русски. Раньше перевода не было вообще — жанр
 * просто не выводили на карточке, хотя фильтр по жанрам существовал.
 *
 * Список сверен с живыми данными: на 2026-07-31 в каталоге встречаются
 * platformer, shooter, adventure, tycoon, puzzle, sandbox, arcade,
 * simulator, survival, rpg, other. Плюс horror, racing, obby —
 * они есть в фильтрах фронта.
 */

export const GENRES = {
  platformer: "Платформер",
  shooter: "Шутер",
  adventure: "Приключение",
  tycoon: "Тайкун",
  puzzle: "Головоломка",
  sandbox: "Песочница",
  arcade: "Аркада",
  simulator: "Симулятор",
  survival: "Выживание",
  rpg: "RPG",
  horror: "Хоррор",
  racing: "Гонки",
  obby: "Обби",
  other: "Другое",
};

/** Человеческое название жанра. Неизвестный код показываем как есть. */
export function genreLabel(code) {
  if (!code) return null;
  return GENRES[code] || code;
}

/** Список для фильтров: [{value, label}]. «Все» добавляет вызывающий. */
export function genreOptions() {
  return Object.entries(GENRES).map(([value, label]) => ({ value, label }));
}

/** Возрастные рейтинги по 436-ФЗ. */
export const AGE_RATINGS = [6, 12, 16, 18];

/**
 * Компактный формат чисел: 1234 → 1.2K, 1500000 → 1.5M.
 * Нужен, чтобы счётчик запусков не ломал вёрстку карточки: у популярной
 * игры их 4554, а место под число фиксированное.
 */
export function fmtCount(n) {
  const v = Number(n) || 0;
  if (v >= 1_000_000) return (v / 1_000_000).toFixed(1).replace(/\.0$/, "") + "M";
  if (v >= 1_000) return (v / 1_000).toFixed(1).replace(/\.0$/, "") + "K";
  return String(v);
}

/** «12 мин» — среднее время игры. 0/undefined → null (не показываем). */
export function fmtPlayTime(seconds) {
  const s = Number(seconds) || 0;
  if (s < 30) return null;
  if (s < 3600) return `${Math.round(s / 60)} мин`;
  const h = Math.floor(s / 3600);
  const m = Math.round((s % 3600) / 60);
  return m ? `${h} ч ${m} мин` : `${h} ч`;
}

/**
 * Статус игры для автора: что с ней происходит.
 * Возвращает {label, tone} или null для опубликованной (её статус
 * очевиден и метка была бы шумом).
 */
export function statusMeta(game) {
  const s = game?.status;
  if (s === "published") return null;
  if (s === "draft") return { label: "Черновик", tone: "neutral" };
  if (s === "pending") return { label: "На проверке", tone: "warning" };
  if (s === "blocked" || s === "rejected") {
    return { label: "Отклонена", tone: "danger" };
  }
  return null;
}

/**
 * Текст кнопки запуска — функция состояния, а не константа.
 * Приём из школы: человек по кнопке понимает, что произойдёт.
 */
export function playLabel({ played = false, busy = false } = {}) {
  if (busy) return "Запускаем…";
  return played ? "Продолжить" : "Играть";
}
